import axios from 'axios';
import type { AxiosResponse } from 'axios';
import type {
  CreateTenantBody,
  CreateUserBody,
  EntityResponse,
  OAuthClientSecret,
  PageResponse,
  Role,
  SeriesItem,
  Tenant,
  UpdateTenantBody,
  UpdateUserBody,
  User,
} from './types';
import { loadConfig } from '@api/api';

type QueryValue = string | number | boolean | undefined | null;
type QueryParams = Record<string, QueryValue>;

async function getApiBaseUrl() {
  const runtimeBaseUrl = await loadConfig();
  if (!runtimeBaseUrl) {
    throw new Error('Backoffice runtime API base URL is unavailable.');
  }
  return `${runtimeBaseUrl}/api/v1`;
}

function authHeaders(accessToken: string) {
  return {
    Accept: '*/*',
    Authorization: `Bearer ${accessToken}`,
  };
}

function encodeParams(params?: QueryParams) {
  if (!params) return undefined;

  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    searchParams.set(key, String(value));
  });

  return searchParams;
}

export async function getBackofficeSeries({
  accessToken,
  params,
}: {
  accessToken: string;
  params?: QueryParams;
}) {
  const apiBaseUrl = await getApiBaseUrl();
  const response: AxiosResponse<PageResponse<SeriesItem>> = await axios({
    method: 'get',
    url: `${apiBaseUrl}/series`,
    params: encodeParams(params),
    headers: authHeaders(accessToken),
  });

  return response.data;
}

export async function exportBackofficeSeries({
  accessToken,
  id,
}: {
  accessToken: string;
  id: SeriesItem['id'];
}) {
  const apiBaseUrl = await getApiBaseUrl();
  return axios({
    method: 'get',
    url: `${apiBaseUrl}/series/export/${id}`,
    headers: authHeaders(accessToken),
    timeout: 300000,
    responseType: 'blob',
  });
}

export async function getBackofficeTenants({
  accessToken,
}: {
  accessToken: string;
}) {
  const apiBaseUrl = await getApiBaseUrl();
  const response: AxiosResponse<PageResponse<Tenant>> = await axios({
    method: 'get',
    url: `${apiBaseUrl}/tenant`,
    headers: authHeaders(accessToken),
  });

  return response.data.data;
}

export async function createBackofficeTenant({
  accessToken,
  body,
}: {
  accessToken: string;
  body: CreateTenantBody;
}) {
  const apiBaseUrl = await getApiBaseUrl();
  const response: AxiosResponse<EntityResponse<Tenant>> = await axios({
    method: 'post',
    url: `${apiBaseUrl}/tenant`,
    headers: authHeaders(accessToken),
    data: body,
  });

  return response.data;
}

export async function updateBackofficeTenant({
  accessToken,
  id,
  body,
}: {
  accessToken: string;
  id: Tenant['id'];
  body: UpdateTenantBody;
}) {
  const apiBaseUrl = await getApiBaseUrl();
  const response: AxiosResponse<EntityResponse<Tenant>> = await axios({
    method: 'patch',
    url: `${apiBaseUrl}/tenant/${id}`,
    headers: authHeaders(accessToken),
    data: body,
  });

  return response.data;
}

export async function deleteBackofficeTenant({
  accessToken,
  id,
}: {
  accessToken: string;
  id: Tenant['id'];
}) {
  const apiBaseUrl = await getApiBaseUrl();
  const response: AxiosResponse<void> = await axios({
    method: 'delete',
    url: `${apiBaseUrl}/tenant/${id}`,
    headers: authHeaders(accessToken),
  });

  return response.data;
}

export async function createBackofficeOAuthClient({
  accessToken,
  tenantId,
}: {
  accessToken: string;
  tenantId: Tenant['id'];
}) {
  const apiBaseUrl = await getApiBaseUrl();
  const response: AxiosResponse<OAuthClientSecret> = await axios({
    method: 'post',
    url: `${apiBaseUrl}/oauth/client`,
    headers: authHeaders(accessToken),
    data: { tenantId },
  });

  return response.data;
}

export async function getBackofficeUsers({
  accessToken,
  params,
}: {
  accessToken: string;
  params?: QueryParams;
}) {
  const apiBaseUrl = await getApiBaseUrl();
  const response: AxiosResponse<PageResponse<User>> = await axios({
    method: 'get',
    url: `${apiBaseUrl}/user`,
    params: encodeParams(params),
    headers: authHeaders(accessToken),
  });

  return response.data;
}

export async function getBackofficeRoles({
  accessToken,
}: {
  accessToken: string;
}) {
  const apiBaseUrl = await getApiBaseUrl();
  const response: AxiosResponse<PageResponse<Role>> = await axios({
    method: 'get',
    url: `${apiBaseUrl}/roles`,
    headers: authHeaders(accessToken),
  });

  return response.data.data;
}

export async function createBackofficeUser({
  accessToken,
  body,
}: {
  accessToken: string;
  body: CreateUserBody;
}) {
  const apiBaseUrl = await getApiBaseUrl();
  const response: AxiosResponse<EntityResponse<User>> = await axios({
    method: 'post',
    url: `${apiBaseUrl}/user`,
    headers: authHeaders(accessToken),
    data: body,
  });

  return response.data;
}

export async function updateBackofficeUser({
  accessToken,
  id,
  body,
}: {
  accessToken: string;
  id: User['id'];
  body: UpdateUserBody;
}) {
  const apiBaseUrl = await getApiBaseUrl();
  const response: AxiosResponse<EntityResponse<User>> = await axios({
    method: 'patch',
    url: `${apiBaseUrl}/user/${id}`,
    headers: authHeaders(accessToken),
    data: body,
  });

  return response.data;
}

export async function deleteBackofficeUser({
  accessToken,
  id,
}: {
  accessToken: string;
  id: User['id'];
}) {
  const apiBaseUrl = await getApiBaseUrl();
  const response: AxiosResponse<void> = await axios({
    method: 'delete',
    url: `${apiBaseUrl}/user/${id}`,
    headers: authHeaders(accessToken),
  });

  return response.data;
}
